/* Aksheya theme — vanilla web components, no dependencies */
(() => {
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const mqDesktop = window.matchMedia('(min-width: 990px)');
  const parser = new DOMParser();
  const parseHTML = (html) => parser.parseFromString(html, 'text/html');

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const toast = (msg) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 2600);
  };

  /* ---------------------------------------------------------------------
     Drawers
     --------------------------------------------------------------------- */
  const overlay = $('[data-overlay]');
  let openDrawer = null;
  let lastFocus = null;

  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      // Drawers rendered inside stacking contexts (e.g. sticky header) are moved to <body>.
      if (this.hasAttribute('data-portal') && this.parentElement !== document.body) {
        $$(`#${this.id}`).forEach((el) => { if (el !== this && el.parentElement === document.body) el.remove(); });
        document.body.appendChild(this);
        return;
      }
      if (this._ready) return;
      this._ready = true;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-close-drawer]')) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trap(e);
      });
    }
    open(opener) {
      if (openDrawer && openDrawer !== this) openDrawer.close(false);
      lastFocus = opener || document.activeElement;
      hydrateImages(this); // deferred pictures inside the drawer (e.g. mobile menu images)
      this.classList.add('is-open');
      overlay.hidden = false;
      requestAnimationFrame(() => overlay.classList.add('is-visible'));
      document.body.classList.add('is-locked');
      $$(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      openDrawer = this;
      setTimeout(() => {
        const focusTarget = $('[data-predictive-input]', this) || this;
        focusTarget.focus({ preventScroll: true });
      }, 50);
    }
    close(restore = true) {
      this.classList.remove('is-open');
      $$(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (openDrawer === this) {
        openDrawer = null;
        overlay.classList.remove('is-visible');
        setTimeout(() => { if (!openDrawer) overlay.hidden = true; }, 300);
        document.body.classList.remove('is-locked');
        if (restore && lastFocus) lastFocus.focus({ preventScroll: true });
      }
    }
    trap(e) {
      const focusables = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', this)
        .filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  customElements.define('drawer-panel', DrawerPanel);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-drawer]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.openDrawer);
      if (drawer) {
        e.preventDefault();
        drawer.open(opener);
      }
    }
  });
  overlay?.addEventListener('click', () => openDrawer?.close());

  /* ---------------------------------------------------------------------
     Header
     --------------------------------------------------------------------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = $('.site-header', this);
      this.transparent = this.dataset.transparent === 'true';
      if (this.transparent) document.body.classList.add('has-transparent-header');
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.setHeight();
      const ro = new ResizeObserver(() => this.setHeight());
      ro.observe(this.header);
      const announce = document.querySelector('.announcement');
      if (announce) ro.observe(announce);

      $$('.site-nav__item', this).forEach((item) => {
        if (!$('.mega', item)) return;
        item.addEventListener('mouseenter', () => this.classList.add('is-menu-open'));
        item.addEventListener('mouseleave', () => this.classList.remove('is-menu-open'));
        item.addEventListener('focusin', () => this.classList.add('is-menu-open'));
        item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) this.classList.remove('is-menu-open'); });
      });
    }
    setHeight() {
      const root = document.documentElement;
      root.style.setProperty('--header-h', `${this.header.offsetHeight}px`);
      const announce = document.querySelector('.announcement');
      root.style.setProperty('--announce-h', `${announce ? announce.offsetHeight : 0}px`);
      const inner = $('.site-header__inner', this);
      const cats = $('.mobile-cats', this);
      if (inner) this.header.style.setProperty('--logo-row-h', `${inner.offsetHeight - 1}px`);
      this.header.style.setProperty('--cats-h', `${cats && !mqDesktop.matches ? cats.parentElement.offsetHeight : 0}px`);
    }
    onScroll() {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (!this._progressTick) {
        this._progressTick = true;
        requestAnimationFrame(() => {
          this._progressTick = false;
          const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
          this.header.style.setProperty('--scroll', p.toFixed(4));
        });
      }
      this.classList.toggle('is-scrolled', y > 40);
      if (this.dataset.hideOnScroll === 'true' && !mqDesktop.matches) {
        const last = this.lastY || 0;
        if (y > last + 6 && y > 220) this.classList.add('is-hidden');
        else if (y < last - 6 || y < 220) this.classList.remove('is-hidden');
      } else {
        this.classList.remove('is-hidden');
      }
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__item', this);
      if (this.items.length < 2) return;
      this.index = 0;
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      $('[data-prev]', this)?.addEventListener('click', () => this.go(this.index - 1, true));
      $('[data-next]', this)?.addEventListener('click', () => this.go(this.index + 1, true));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.play());
      this.play();
    }
    go(i, user) {
      this.items[this.index].classList.remove('is-active');
      this.index = (i + this.items.length) % this.items.length;
      this.items[this.index].classList.add('is-active');
      if (user) this.play();
    }
    play() { this.stop(); this.timer = setInterval(() => this.go(this.index + 1), this.speed); }
    stop() { clearInterval(this.timer); }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------------------------------------------------------------------
     Category accordion: the hovered/focused panel stays open (desktop)
     --------------------------------------------------------------------- */
  class CategoryAccordion extends HTMLElement {
    connectedCallback() {
      const open = (e) => {
        const item = e.target.closest('.cat-acc__item');
        if (!item || item.classList.contains('is-active')) return;
        $$('.cat-acc__item.is-active', this).forEach((el) => el.classList.remove('is-active'));
        item.classList.add('is-active');
      };
      this.addEventListener('pointerover', (e) => { if (e.pointerType === 'mouse') open(e); });
      this.addEventListener('focusin', (e) => { if (!this.touched) open(e); });
      this.addEventListener('keydown', () => { this.touched = false; });
      // touch: the first tap on a closed panel opens it, a tap on the open panel follows the link
      this.addEventListener('click', (e) => {
        const item = e.target.closest('.cat-acc__item');
        if (!item || item.classList.contains('is-active') || !this.touched) return;
        e.preventDefault();
        open(e);
      });
      this.addEventListener('pointerdown', (e) => { this.touched = e.pointerType !== 'mouse'; });
    }
  }
  customElements.define('category-accordion', CategoryAccordion);

  /* ---------------------------------------------------------------------
     Hero slideshow
     --------------------------------------------------------------------- */
  // Swap deferred image URLs (data-src / data-srcset) into place
  const hydrateImages = (root) => {
    $$('[data-srcset], [data-src]', root).forEach((el) => {
      if (el.dataset.srcset) { el.srcset = el.dataset.srcset; delete el.dataset.srcset; }
      if (el.dataset.src) { el.src = el.dataset.src; delete el.dataset.src; }
    });
  };
  const afterLoad = (fn, delay = 0) => {
    const run = () => setTimeout(() => ('requestIdleCallback' in window ? requestIdleCallback(fn) : fn()), delay);
    if (document.readyState === 'complete') run(); else window.addEventListener('load', run, { once: true });
  };

  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.hero__slide', this);
      this.dots = $$('.hero__dot', this);
      this.index = 0;
      if (this.slides.length < 2) return;
      // the next slide's image arrives after the page has settled, never competing with the first paint
      afterLoad(() => this.prepare(1), 1500);
      this.speed = (parseInt(this.dataset.speed, 10) || 6) * 1000;
      this.dots.forEach((d) => d.addEventListener('click', () => this.go(parseInt(d.dataset.dot, 10))));
      let startX = 0;
      this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 50) this.go(this.index + (dx < 0 ? 1 : -1));
      });
      if (this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.autoplay = true;
        this.addEventListener('mouseenter', () => { this.classList.add('is-paused'); this.stop(); });
        this.addEventListener('mouseleave', () => { this.classList.remove('is-paused'); this.play(); });
        this.play();
      }
    }
    go(i) {
      this.slides[this.index].classList.remove('is-active');
      this.dots[this.index]?.classList.remove('is-active');
      this.index = (i + this.slides.length) % this.slides.length;
      const slide = this.slides[this.index];
      hydrateImages(slide);
      $$('img[loading="lazy"]', slide).forEach((img) => { img.loading = 'eager'; });
      slide.classList.add('is-active');
      const dot = this.dots[this.index];
      if (dot) { dot.classList.remove('is-active'); void dot.offsetWidth; dot.classList.add('is-active'); }
      if (this.autoplay) this.play();
    }
    prepare(i) {
      const slide = this.slides[(i + this.slides.length) % this.slides.length];
      hydrateImages(slide);
      $$('img[loading="lazy"]', slide).forEach((img) => { img.loading = 'eager'; });
    }
    play() {
      this.stop();
      this.timer = setTimeout(() => this.go(this.index + 1), this.speed);
      this.prepTimer = setTimeout(() => this.prepare(this.index + 1), Math.max(0, this.speed - 2500));
    }
    stop() { clearTimeout(this.timer); clearTimeout(this.prepTimer); }
  }
  customElements.define('hero-slideshow', HeroSlideshow);

  // Mega menu pictures load once the pointer reaches the header (or the menu gets focus)
  const siteNav = $('.site-nav');
  if (siteNav) {
    const header = siteNav.closest('header, .site-header') || siteNav;
    const loadMega = () => hydrateImages(siteNav);
    header.addEventListener('pointerenter', loadMega, { once: true });
    siteNav.addEventListener('focusin', loadMega, { once: true });
  }

  /* ---------------------------------------------------------------------
     Carousel
     --------------------------------------------------------------------- */
  class CarouselSlider extends HTMLElement {
    connectedCallback() {
      this.track = $('.carousel__track', this);
      this.progress = $('.carousel__progress span', this);
      this.nav = $(`[data-carousel-nav="${this.id}"]`);
      if (!this.track) return;
      if (this.nav) {
        this.prev = $('[data-carousel-prev]', this.nav);
        this.next = $('[data-carousel-next]', this.nav);
        this.prev.addEventListener('click', () => this.scrollBy(-1));
        this.next.addEventListener('click', () => this.scrollBy(1));
      }
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { passive: true });
      new ResizeObserver(this.update).observe(this.track);
    }
    scrollBy(dir) {
      const item = this.track.firstElementChild;
      if (!item) return;
      const step = item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0);
      const visible = Math.max(1, Math.floor(this.track.clientWidth / step));
      this.track.scrollBy({ left: dir * step * visible, behavior: 'smooth' });
    }
    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      const scrollable = max > 4;
      if (this.nav) {
        this.nav.style.display = scrollable && mqDesktop.matches ? 'flex' : 'none';
        this.prev.disabled = scrollLeft <= 4;
        this.next.disabled = scrollLeft >= max - 4;
      }
      if (this.progress) {
        this.progress.parentElement.style.visibility = scrollable ? 'visible' : 'hidden';
        const w = Math.min(100, (clientWidth / scrollWidth) * 100);
        const ratio = max > 0 ? scrollLeft / max : 0;
        this.progress.style.setProperty('--progress-w', `${w}%`);
        this.progress.style.setProperty('--progress-x', `${ratio * (100 / w - 1) * 100}%`);
      }
    }
  }
  customElements.define('carousel-slider', CarouselSlider);

  class TestimonialRotator extends HTMLElement {
    connectedCallback() {
      this.items = $$('.quote', this);
      this.dots = $$('[data-dot]', this);
      if (this.items.length < 2) return;
      this.index = 0;
      this.speed = (parseInt(this.dataset.speed, 10) || 7) * 1000;
      this.dots.forEach((d) => d.addEventListener('click', () => this.go(parseInt(d.dataset.dot, 10))));
      this.play();
    }
    go(i) {
      this.items[this.index].classList.remove('is-active');
      this.dots[this.index].classList.remove('is-active');
      this.index = (i + this.items.length) % this.items.length;
      this.items[this.index].classList.add('is-active');
      this.dots[this.index].classList.add('is-active');
      this.play();
    }
    play() { clearTimeout(this.timer); this.timer = setTimeout(() => this.go(this.index + 1), this.speed); }
  }
  customElements.define('testimonial-rotator', TestimonialRotator);

  /* ---------------------------------------------------------------------
     Quantity input
     --------------------------------------------------------------------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      $('[data-qty-minus]', this).addEventListener('click', () => this.step(-1));
      $('[data-qty-plus]', this).addEventListener('click', () => this.step(1));
    }
    step(n) {
      const min = parseInt(this.input.min, 10) || 0;
      const val = Math.max(min, (parseInt(this.input.value, 10) || 0) + n);
      this.input.value = val;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------------------------------------------------------------------
     Cart
     --------------------------------------------------------------------- */
  const Cart = {
    sections() {
      const list = ['cart-drawer'];
      const page = $('cart-page');
      if (page) list.push(page.dataset.section);
      return list;
    },
    async add(formData) {
      formData.append('sections', this.sections().join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      await this.render(data.sections);
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sections(), sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      this.updateCount(data.item_count);
      await this.render(data.sections);
      return data;
    },
    async render(sections) {
      if (!sections) {
        const res = await fetch(`${window.location.pathname}?sections=${this.sections().join(',')}`);
        sections = await res.json();
      }
      const drawerHTML = sections['cart-drawer'];
      if (drawerHTML) {
        const doc = parseHTML(drawerHTML);
        const target = $('[data-cart-drawer-content]');
        const src = doc.querySelector('.shopify-section') || doc.body;
        if (target) target.innerHTML = src.innerHTML;
      }
      const page = $('cart-page');
      if (page && sections[page.dataset.section]) {
        const doc = parseHTML(sections[page.dataset.section]);
        const src = doc.querySelector('[data-update="cart-page"]');
        const dst = $('[data-update="cart-page"]', page);
        if (src && dst) dst.innerHTML = src.innerHTML;
      }
      const count = $$('.cart-item', $('[data-cart-drawer-content]') || document).reduce(
        (sum, el) => sum + (parseInt($('[data-line-input]', el)?.value, 10) || 0), 0,
      );
      this.updateCount(count);
    },
    updateCount(count) {
      const prev = parseInt($('[data-cart-count]')?.textContent, 10) || 0;
      $$('[data-cart-count]').forEach((el) => { el.textContent = count > 0 ? count : ''; });
      if (count > prev) {
        const bar = $('[data-app-bar]');
        bar?.classList.remove('is-bump');
        void bar?.offsetWidth;
        bar?.classList.add('is-bump');
        if (navigator.vibrate) navigator.vibrate(10);
      }
      $$('[data-cart-count-label]').forEach((el) => { el.textContent = `(${count})`; });
    },
    open() {
      if (theme.cartType === 'drawer') $('#CartDrawer')?.open();
      else window.location.href = theme.routes.cart;
    },
  };

  // Line item quantity / remove (drawer + page)
  const onLineChange = async (item, qty) => {
    item.classList.add('is-loading');
    try {
      await Cart.change(parseInt(item.dataset.line, 10), qty);
    } catch (err) {
      toast(err.message);
      item.classList.remove('is-loading');
    }
  };
  document.addEventListener('change', debounce((e) => {
    const input = e.target.closest('[data-line-input]');
    if (!input) return;
    onLineChange(input.closest('.cart-item'), parseInt(input.value, 10) || 0);
  }, 350));
  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-remove]');
    if (remove) onLineChange(remove.closest('.cart-item'), 0);
  });
  document.addEventListener('change', debounce((e) => {
    if (!e.target.matches('[data-cart-note]')) return;
    fetch(`${theme.routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: e.target.value }),
    });
  }, 500));

  // Quick add from product cards
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    btn.classList.add('is-loading');
    const fd = new FormData();
    fd.append('id', btn.dataset.quickAdd);
    fd.append('quantity', '1');
    try {
      await Cart.add(fd);
      Cart.open();
    } catch (err) {
      toast(err.message);
    } finally {
      btn.classList.remove('is-loading');
    }
  });

  // Product form submit (Ajax)
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || theme.cartType !== 'drawer') return;
    e.preventDefault();
    const btn = $('[data-add-button]', form);
    const error = $('[data-form-error]', form);
    error.hidden = true;
    const invalid = $$(`[form="${form.getAttribute('id')}"][required]:not([disabled]), [required]:not([disabled])`, form.ownerDocument)
      .filter((el) => el.form === form).find((el) => !el.checkValidity());
    if (invalid) {
      invalid.reportValidity();
      invalid.focus();
      return;
    }
    btn.classList.add('is-loading');
    try {
      await Cart.add(new FormData(form));
      Cart.open();
    } catch (err) {
      error.textContent = err.message;
      error.hidden = false;
    } finally {
      btn.classList.remove('is-loading');
    }
  });

  /* ---------------------------------------------------------------------
     Product page
     --------------------------------------------------------------------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse($('[data-variants]', this).textContent);
      this.addEventListener('change', () => this.onChange());
    }
    selectedOptions() {
      return $$('fieldset', this).map((fs) => $('input:checked', fs)?.value);
    }
    onChange() {
      const options = this.selectedOptions();
      $$('[data-option-selected]', this).forEach((el, i) => { el.textContent = options[i] || ''; });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
      this.updateAvailability(options);
      this.closest('product-info')?.setVariant(variant);
    }
    updateAvailability(selected) {
      $$('fieldset', this).forEach((fs, index) => {
        $$('input', fs).forEach((input) => {
          const available = this.variants.some((v) => v.available
            && v.options[index] === input.value
            && v.options.every((o, i) => i === index || i > index || o === selected[i]));
          input.classList.toggle('is-unavailable', !available);
        });
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.form = $('[data-product-form]', this);
      this.idInput = $('[data-variant-id]', this);
      this.button = $('[data-add-button]', this);
      this.gallery = $('media-gallery', this);
      this.sticky = $('sticky-atc');
    }
    async setVariant(variant) {
      const tailoring = $('[data-tailoring-note]', this);
      if (!variant) {
        this.button.disabled = true;
        this.button.textContent = theme.strings.unavailable;
        return;
      }
      if (tailoring) {
        const isTailor = tailoring.dataset.variantIds.split(',').includes(String(variant.id));
        tailoring.hidden = !isTailor;
        const measure = $('[data-measure]', this);
        if (measure) {
          measure.hidden = !isTailor;
          $$('[data-measure-input]', measure).forEach((el) => { el.disabled = !isTailor; });
        }
      }
      this.idInput.value = variant.id;
      const sku = $('[data-sku]', this);
      if (sku) {
        sku.hidden = !variant.sku;
        $('[data-sku-value]', sku).textContent = variant.sku || '';
      }
      this.button.disabled = !variant.available;
      this.button.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      if (variant.featured_media && this.gallery) this.gallery.scrollToMedia(variant.featured_media.id);
      this.sticky?.update(variant, this.button.disabled);

      try {
        const res = await fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${this.dataset.section}`);
        const doc = parseHTML(await res.text());
        ['price', 'installments', 'stock'].forEach((key) => {
          const src = doc.querySelector(`[data-update="${key}"]`);
          const dst = $(`[data-update="${key}"]`, this);
          if (src && dst) dst.innerHTML = src.innerHTML;
        });
        const stickyVariant = doc.querySelector('[data-sticky-variant]');
        if (stickyVariant && this.sticky) $('[data-sticky-variant]', this.sticky).innerHTML = stickyVariant.innerHTML;
      } catch (e) { /* keep client-side state */ }
    }
  }
  customElements.define('product-info', ProductInfo);

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      this.counter = $('[data-gallery-counter]', this);
      this.dots = $$('.gallery__dots span', this);
      this.items = $$('.gallery__item', this);
      this.track.addEventListener('scroll', debounce(() => this.onScroll(), 60), { passive: true });
      this.items.forEach((item, i) => item.addEventListener('click', () => this.openLightbox(i)));
    }
    onScroll() {
      const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.items.length}`;
      this.dots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
    }
    scrollToMedia(id) {
      const item = $(`[data-media-id="${id}"]`, this);
      if (!item) return;
      if (mqDesktop.matches) {
        if (item.dataset.index !== '0') {
          this.track.prepend(item);
          this.items = $$('.gallery__item', this);
          this.items.forEach((el, i) => { el.dataset.index = i; });
        }
      } else {
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      }
    }
    openLightbox(index) {
      const lb = $('#Lightbox');
      if (!lb) return;
      const media = this.items[index];
      if (!media || !$('img', media)) return;
      const imgIndex = this.items.slice(0, index).filter((el) => $('img', el)).length;
      lb.classList.add('is-open');
      document.body.classList.add('is-locked');
      const track = $('[data-lightbox-track]', lb);
      $$('img', track).forEach((img) => { img.loading = 'eager'; });
      requestAnimationFrame(() => { track.scrollLeft = track.clientWidth * imgIndex; });
      const close = () => {
        lb.classList.remove('is-open');
        document.body.classList.remove('is-locked');
        document.removeEventListener('keydown', onKey);
      };
      const onKey = (e) => { if (e.key === 'Escape') close(); };
      $('[data-lightbox-close]', lb).onclick = close;
      document.addEventListener('keydown', onKey);
    }
  }
  customElements.define('media-gallery', MediaGallery);

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.mainButton = $('[data-add-button]');
      if (!this.mainButton) return;
      $('[data-sticky-add]', this).addEventListener('click', () => {
        const form = $('[data-product-form]');
        const picker = $('variant-picker');
        const needsChoice = picker && $$('fieldset', picker).some((fs) => !$('input:checked', fs));
        if (needsChoice || this.mainButton.disabled) {
          form.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        form.requestSubmit(this.mainButton);
      });
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
        document.body.classList.toggle('has-sticky-atc', show);
      }).observe(this.mainButton);
    }
    update(variant, disabled) {
      const btn = $('[data-sticky-add]', this);
      btn.textContent = disabled ? theme.strings.soldOut : theme.strings.addToCart;
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      new IntersectionObserver((entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        fetch(this.dataset.url).then((r) => r.text()).then((html) => {
          const el = parseHTML(html).querySelector('product-recommendations');
          if (el && el.innerHTML.trim()) {
            this.innerHTML = el.innerHTML;
            initReveal(this);
          } else {
            this.remove();
          }
        });
      }, { rootMargin: '0px 0px 400px 0px' }).observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------------------------------------------------------------------
     Predictive search
     --------------------------------------------------------------------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('[data-predictive-input]', this);
      this.results = $('[data-predictive-results]', this);
      this.suggest = $('[data-search-suggest]', this);
      this.input.addEventListener('input', debounce(() => this.search(), 280));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        this.suggest.hidden = false;
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=4&resources[options][fields]=title,product_type,variants.title,tag&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.controller.signal });
        const doc = parseHTML(await res.text());
        const src = doc.querySelector('.shopify-section') || doc.body;
        this.results.innerHTML = src.innerHTML;
        this.suggest.hidden = true;
      } catch (e) { /* aborted */ }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------------------------------------------------------------------
     Collection filters, sort, load more, grid
     --------------------------------------------------------------------- */
  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.form = $('[data-facets-form]', this);
      this.onFormChange = debounce(() => this.apply(this.formURL()), 450);
      this.form?.addEventListener('input', this.onFormChange);
      $('[data-sort]', this)?.addEventListener('change', (e) => {
        $('[data-sort-hidden]', this).value = e.target.value;
        this.apply(this.formURL());
      });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-filter-link]');
        if (link) { e.preventDefault(); this.apply(link.href); return; }
        const more = e.target.closest('[data-load-more]');
        if (more) { e.preventDefault(); this.loadMore(more); return; }
        const grid = e.target.closest('[data-grid]');
        if (grid) this.setGrid(grid.dataset.grid);
      });
      window.addEventListener('popstate', () => this.apply(window.location.href, false));
      try {
        const saved = localStorage.getItem('aksheya-grid');
        if (saved) this.setGrid(saved);
      } catch (e) { /* storage blocked */ }
    }
    formURL() {
      const params = new URLSearchParams(new FormData(this.form));
      for (const [k, v] of Array.from(params.entries())) if (v === '') params.delete(k);
      return `${this.form.action}?${params.toString()}`;
    }
    async apply(url, push = true) {
      const grid = $('[data-product-grid]', this);
      grid?.classList.add('is-loading');
      const u = new URL(url, window.location.origin);
      u.searchParams.delete('page');
      const fetchURL = new URL(u);
      fetchURL.searchParams.set('section_id', this.dataset.section);
      const doc = parseHTML(await (await fetch(fetchURL)).text());
      ['grid', 'pagination', 'active-filters', 'count', 'apply-label'].forEach((key) => this.swap(doc, key));
      const facetsDst = $('[data-update="facets"]', this);
      const facetsSrc = doc.querySelector('[data-update="facets"]');
      if (facetsDst && facetsSrc) {
        const open = $$('details', facetsDst).map((d) => d.open);
        const focusedName = document.activeElement?.name;
        facetsDst.innerHTML = facetsSrc.innerHTML;
        $$('details', facetsDst).forEach((d, i) => { if (open[i] !== undefined) d.open = open[i]; });
        if (focusedName) $(`[name="${CSS.escape(focusedName)}"]`, facetsDst)?.focus({ preventScroll: true });
      }
      const filterBtn = $('[data-open-drawer="FacetsDrawer"]', this);
      const filterSrc = doc.querySelector('[data-open-drawer="FacetsDrawer"]');
      if (filterBtn && filterSrc) filterBtn.innerHTML = filterSrc.innerHTML;
      this.restoreGrid();
      initReveal(this);
      if (push) window.history.pushState({}, '', u);
    }
    swap(doc, key) {
      const src = doc.querySelector(`[data-update="${key}"]`);
      const dst = $(`[data-update="${key}"]`, this);
      if (src && dst) dst.innerHTML = src.innerHTML;
    }
    async loadMore(btn) {
      btn.classList.add('is-loading');
      const url = new URL(btn.href);
      url.searchParams.set('section_id', this.dataset.section);
      const doc = parseHTML(await (await fetch(url)).text());
      const items = doc.querySelectorAll('[data-product-grid] > li');
      const grid = $('[data-product-grid]', this);
      items.forEach((li) => grid.appendChild(li));
      this.swap(doc, 'pagination');
      initReveal(this);
    }
    setGrid(cols) {
      this.gridCols = cols;
      $$('[data-grid]', this).forEach((b) => b.classList.toggle('is-active', b.dataset.grid === cols));
      this.restoreGrid();
      try { localStorage.setItem('aksheya-grid', cols); } catch (e) { /* storage blocked */ }
    }
    restoreGrid() {
      const grid = $('[data-product-grid]', this);
      if (grid && this.gridCols) {
        grid.style.setProperty('--cols-desktop', this.gridCols);
        grid.style.setProperty('--cols-tablet', Math.min(3, this.gridCols));
      }
    }
  }
  customElements.define('collection-filters', CollectionFilters);

  /* ---------------------------------------------------------------------
     Scroll scenes: one rAF loop sets --p (0→1) on visible scenes
     --------------------------------------------------------------------- */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const swipeQuery = window.matchMedia('(max-width: 989px), (hover: none)');
  const activeScenes = new Set();
  let sceneTicking = false;
  const clamp01 = (v) => Math.min(1, Math.max(0, v));

  class ScrollScene extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'pin';
      if (this.mode === 'rail') this.setupRail();
      if (this.mode === 'postcards') this.setupPostcards();
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) activeScenes.add(this); else activeScenes.delete(this);
        requestSceneFrame();
      }, { rootMargin: '20% 0px 20% 0px' });
      this.observer.observe(this);
      if (reduceMotion.matches) this.style.setProperty('--p', 1);
    }
    disconnectedCallback() { this.observer?.disconnect(); activeScenes.delete(this); }
    setupRail() {
      this.track = $('[data-rail-track]', this);
      this.viewport = $('.rail__viewport', this);
      this.counter = $('[data-rail-current]', this);
      this.items = $$('.rail__item', this);
      this.reverse = this.dataset.reverse === 'true';
      const layout = () => {
        // Scroll-driven travel on every screen; native swipe only when motion is reduced
        const swipe = reduceMotion.matches;
        this.classList.toggle('rail--swipe', swipe);
        this.layoutRoad();
        if (swipe) {
          this.style.height = '';
          this.track.style.paddingRight = '';
          if (this.reverse && !this._swipeInit) {
            this._swipeInit = true;
            requestAnimationFrame(() => { this.viewport.scrollLeft = this.viewport.scrollWidth; });
          }
          return;
        }
        // Forward mode: the first card enters from the right edge of the screen, and the end padding
        // lets the last card travel all the way to the centre of the screen.
        const first = this.items[0];
        const pad = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
        if (!this.reverse && first) {
          this.track.style.paddingRight = `${Math.max(pad, (this.viewport.clientWidth - first.offsetWidth) / 2)}px`;
        }
        this.distance = Math.max(0, this.track.offsetWidth - this.viewport.clientWidth);
        this.enter = this.reverse || !first ? 0 : Math.max(0, this.viewport.clientWidth - first.offsetWidth - pad * 2);
        // Hold at the end so the last card stays centred for as long as the others
        const pitch = this.items[1] ? this.items[1].offsetLeft - this.items[0].offsetLeft : 0;
        this.hold = this.reverse ? 0 : pitch / 2;
        this.style.height = `${window.innerHeight + this.distance + this.enter + this.hold}px`;
        this.update();
      };
      layout();
      new ResizeObserver(layout).observe(this.track);
      swipeQuery.addEventListener('change', layout);
      window.addEventListener('resize', layout);
      this.viewport.addEventListener('scroll', () => {
        if (!this.classList.contains('rail--swipe')) return;
        const max = this.viewport.scrollWidth - this.viewport.clientWidth;
        let p = max > 0 ? this.viewport.scrollLeft / max : 0;
        if (this.reverse) p = 1 - p;
        this.style.setProperty('--p', p.toFixed(4));
        this.updateCounter(p);
      }, { passive: true });
    }
    setupPostcards() {
      // The final photo starts clipped to the bottom card's photo slot
      const measure = () => {
        const slot = $('[data-pc-slot]', this);
        const box = $('.pc__sticky', this);
        if (!slot || !box) return;
        const b = box.getBoundingClientRect();
        // read the slot without the card transforms
        const card = slot.closest('.pc__card');
        const prev = card.style.transform;
        card.style.transform = 'none';
        const s = slot.getBoundingClientRect();
        card.style.transform = prev;
        this.style.setProperty('--st', `${s.top - b.top}px`);
        this.style.setProperty('--sl', `${s.left - b.left}px`);
        this.style.setProperty('--sr', `${b.right - s.right}px`);
        this.style.setProperty('--sb', `${b.bottom - s.bottom}px`);
      };
      measure();
      new ResizeObserver(measure).observe(this);
      window.addEventListener('resize', measure);
      if (!reduceMotion.matches) this.setupToss();
      // decode the postcards and the big final photo about a screen ahead (after the page has loaded),
      // so scrolling into the scene doesn't stall but the first paint isn't slowed either
      afterLoad(() => {
        const pre = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          pre.disconnect();
          $$('img', this).forEach((img) => { img.loading = 'eager'; img.decode?.().catch(() => {}); });
        }, { rootMargin: '80% 0px 80% 0px' });
        pre.observe(this);
      });
    }
    resetCard(card) {
      delete card.dataset.tossed;
      ['--gx', '--gy', '--gr', '--tx', '--ty', '--tr'].forEach((v) => card.style.removeProperty(v));
    }
    // Grab the top card and throw it in any direction; the scroll position then catches up
    setupToss() {
      const stack = $('[data-pc-stack]', this);
      if (!stack) return;
      let drag = null;
      const setGrab = (card, x, y) => {
        card.style.setProperty('--gx', `${x}px`);
        card.style.setProperty('--gy', `${y}px`);
        card.style.setProperty('--gr', `${x * 0.05}deg`);
      };
      stack.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        const card = e.target.closest('[data-pc-card]');
        if (!card || !card.classList.contains('is-top')) return;
        this.pcInteracted = true;
        clearTimeout(this.pcNudgeTimer);
        drag = { card, id: e.pointerId, x0: e.clientX, y0: e.clientY, dx: 0, dy: 0, pts: [{ x: e.clientX, y: e.clientY, t: e.timeStamp }] };
        card.setPointerCapture(e.pointerId);
        card.classList.add('is-dragging');
        if (e.pointerType === 'mouse') e.preventDefault();
      });
      stack.addEventListener('pointermove', (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        drag.dx = e.clientX - drag.x0;
        drag.dy = e.clientY - drag.y0;
        drag.pts.push({ x: e.clientX, y: e.clientY, t: e.timeStamp });
        if (drag.pts.length > 6) drag.pts.shift();
        setGrab(drag.card, drag.dx, drag.dy);
      });
      const release = (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const { card, dx, dy, pts } = drag;
        drag = null;
        card.classList.remove('is-dragging');
        const a = pts[0];
        const b = pts[pts.length - 1];
        const dt = Math.max(1, b.t - a.t);
        const vx = (b.x - a.x) / dt;
        const vy = (b.y - a.y) / dt;
        const speed = Math.hypot(vx, vy);
        const dist = Math.hypot(dx, dy);
        if (e.type !== 'pointercancel' && (dist > 90 || (speed > 0.5 && dist > 24))) this.toss(card, speed > 0.5 ? vx : dx, speed > 0.5 ? vy : dy);
        else this.snapBack(card, dx, dy);
      };
      stack.addEventListener('pointerup', release);
      stack.addEventListener('pointercancel', release);
    }
    // A small sideways wiggle of the top card hints that it can be grabbed and thrown.
    // Plays shortly after the scene pins, repeats a couple of times, stops once the visitor interacts or scrolls on.
    maybeNudge(ok) {
      if (!ok || this.pcInteracted || (this.pcNudges || 0) >= 3 || reduceMotion.matches) {
        clearTimeout(this.pcNudgeTimer);
        this.pcNudgeTimer = null;
        return;
      }
      if (this.pcNudgeTimer || this.pcNudging) return;
      this.pcNudgeTimer = setTimeout(() => {
        this.pcNudgeTimer = null;
        const card = this.pcCards && this.pcCards[0];
        if (!card || !card.classList.contains('is-top') || this.pcInteracted || this.classList.contains('is-moved')) return;
        this.pcNudges = (this.pcNudges || 0) + 1;
        this.wiggle(card);
      }, this.pcNudges ? 3800 : 800);
    }
    wiggle(card) {
      this.pcNudging = true;
      const start = performance.now();
      const dur = 1300;
      const step = (now) => {
        const k = Math.min(1, (now - start) / dur);
        const stop = this.pcInteracted || card.classList.contains('is-dragging');
        // damped swing: right, left, small right, rest
        const x = stop ? 0 : 30 * Math.sin(k * Math.PI * 3) * Math.pow(1 - k, 1.6);
        if (!stop) {
          card.style.setProperty('--gx', `${x.toFixed(2)}px`);
          card.style.setProperty('--gy', `${(-Math.abs(x) * 0.12).toFixed(2)}px`);
          card.style.setProperty('--gr', `${(x * 0.06).toFixed(3)}deg`);
        }
        if (k < 1 && !stop) requestAnimationFrame(step);
        else {
          if (!card.classList.contains('is-dragging')) ['--gx', '--gy', '--gr'].forEach((v) => card.style.removeProperty(v));
          this.pcNudging = false;
          // queue the next hint if the visitor is still just looking
          this.maybeNudge(this.pcNudgeOk);
        }
      };
      requestAnimationFrame(step);
    }
    snapBack(card, dx, dy) {
      const start = performance.now();
      const step = (now) => {
        const k = Math.min(1, (now - start) / 450);
        const e = 1 - Math.pow(1 - k, 3);
        card.style.setProperty('--gx', `${dx * (1 - e)}px`);
        card.style.setProperty('--gy', `${dy * (1 - e)}px`);
        card.style.setProperty('--gr', `${dx * 0.05 * (1 - e)}deg`);
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
    toss(card, ux, uy) {
      const len = Math.hypot(ux, uy) || 1;
      ux /= len; uy /= len;
      const reach = Math.hypot(window.innerWidth, window.innerHeight);
      card.style.setProperty('--tx', `${ux * reach}px`);
      card.style.setProperty('--ty', `${uy * reach}px`);
      card.style.setProperty('--tr', `${(ux >= 0 ? 1 : -1) * (18 + Math.abs(uy) * 14)}deg`);
      card.dataset.tossed = Date.now();
      // scroll to the point where this card has fully flown away
      const i = Number(card.dataset.pcCard);
      const n = this.pcCards.length;
      const seg = 0.62 / n;
      // the last toss only needs to cross the trigger point; the opening then plays by itself
      const isLast = i === n - 1;
      if (isLast) this.setOpen(true);
      const target = isLast ? 0.04 + i * seg + seg * 0.78 * 0.4 : 0.04 + i * seg + seg * 0.9;
      const rect = this.getBoundingClientRect();
      const top = window.scrollY + rect.top;
      const to = top + target * (rect.height - window.innerHeight);
      const from = window.scrollY;
      if (to <= from) return;
      const start = performance.now();
      const dur = 650;
      this.pcAnimating = true;
      const step = (now) => {
        const k = Math.min(1, (now - start) / dur);
        const e = 1 - Math.pow(1 - k, 3);
        window.scrollTo({ top: from + (to - from) * e, behavior: "instant" });
        if (k < 1) requestAnimationFrame(step);
        else { this.pcAnimating = false; requestSceneFrame(); }
      };
      requestAnimationFrame(step);
    }
    // The final photo opens on a timer once the last card is pushed, instead of following the scroll
    setOpen(open) {
      if (this.pcOpen === open) return;
      this.pcOpen = open;
      if (this.pcOpenRaf) return;
      let last = performance.now();
      const step = (now) => {
        const dt = now - last;
        last = now;
        // ~2.7s to open (card flies off, photo fills the screen, headline rises); closing is quicker
        this.pcK = clamp01((this.pcK || 0) + (this.pcOpen ? dt / 2700 : -dt / 900));
        this.update();
        if ((this.pcOpen && this.pcK < 1) || (!this.pcOpen && this.pcK > 0)) this.pcOpenRaf = requestAnimationFrame(step);
        else this.pcOpenRaf = null;
      };
      this.pcOpenRaf = requestAnimationFrame(step);
    }
    layoutRoad() {
      const road = $('[data-rail-road]', this);
      if (!road || !this.items.length) return;
      const first = this.items[0];
      const last = this.items[this.items.length - 1];
      const dot = (item) => $('.rail__dot', item);
      const center = (item) => item.offsetLeft + dot(item).offsetLeft + dot(item).offsetWidth / 2;
      const stop = $('.rail__stop', first);
      road.style.setProperty('--road-y', `${stop.offsetTop + stop.offsetHeight / 2}px`);
      road.style.setProperty('--road-x', `${center(first)}px`);
      road.style.setProperty('--road-w', `${center(last) - center(first)}px`);
      this.roadBox = { x: center(first), w: center(last) - center(first) };
    }
    updateCounter(p) {
      if (!this.counter || !this.items.length) return;
      const n = this.items.length;
      // index along the travel direction (0 = start of the journey)
      const step = Math.min(n - 1, Math.round(p * (n - 1)));
      this.setCurrent(this.reverse ? n - 1 - step : step);
    }
    setCurrent(domIndex) {
      if (!this.counter || !this.items.length) return;
      this.items.forEach((item, i) => {
        const passed = this.reverse ? i >= domIndex : i <= domIndex;
        item.classList.toggle('is-passed', passed);
        item.classList.toggle('is-current', i === domIndex);
      });
      this.counter.textContent = String(this.items[domIndex].dataset.num || domIndex + 1).padStart(2, '0');
    }
    update() {
      if (reduceMotion.matches) return;
      const r = this.getBoundingClientRect();
      const vh = window.innerHeight;
      let p;
      if (this.mode === 'words') p = clamp01((vh * 0.85 - r.top) / (r.height * 0.85));
      else if (this.mode === 'words-pin') p = clamp01((-r.top + vh * 0.15) / Math.max(1, r.height - vh * 0.85));
      else if (this.mode === 'mask') p = clamp01((vh - r.top) / (vh + r.height));
      else if (this.mode === 'postcards') {
        // Cards are tossed one by one over the first 62% of the pin, the last frame opens, then the title rises
        p = clamp01(-r.top / Math.max(1, r.height - vh));
        const ease = (t) => 1 - Math.pow(1 - t, 3);
        const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
        const cards = this.pcCards || (this.pcCards = $$('[data-pc-card]', this).sort((x, y) => x.dataset.pcCard - y.dataset.pcCard));
        const lines = this.pcLines || (this.pcLines = $$('[data-pc-line]', this));
        const n = cards.length;
        const span = 0.62;
        const seg = n ? span / n : span;
        // pushing the last card about a third of the way starts the timed opening; scrolling back closes it
        const lastStart = 0.04 + Math.max(0, n - 1) * seg;
        const lastQ = clamp01((p - lastStart) / (seg * 0.78));
        if (lastQ >= 0.3) this.setOpen(true);
        else if (lastQ < 0.15 && !this.pcAnimating) this.setOpen(false);
        const ms = (this.pcK || 0) * 2700;
        const fly = ease(clamp01(ms / 700));
        let top = n;
        cards.forEach((card, i) => {
          let f = easeInOut(clamp01((p - 0.04 - i * seg) / (seg * 0.78)));
          if (i === n - 1) f = Math.max(f, fly);
          card.style.setProperty('--f', f.toFixed(4));
          const prev = i === 0 ? 1 : easeInOut(clamp01((p - 0.04 - (i - 1) * seg) / (seg * 0.78)));
          card.style.setProperty('--lift', prev.toFixed(4));
          if (f < 0.5 && top === n) top = i;
          // a tossed card that has been scrolled back home forgets its custom direction
          if (f < 0.01 && card.dataset.tossed && Date.now() - Number(card.dataset.tossed) > 1500) this.resetCard(card);
        });
        cards.forEach((card, i) => card.classList.toggle('is-top', i === top && !this.pcAnimating));
        this.pcTop = top;
        this.classList.toggle('is-moved', p > 0.02);
        // hint at the toss only while the stack is pinned on screen and untouched
        this.pcNudgeOk = r.top <= vh * 0.2 && r.bottom >= vh && p <= 0.02;
        this.maybeNudge(this.pcNudgeOk);
        const last = this.querySelector('.pc__card--last');
        // the frame card settles (straight, full size) before the opening takes over, so there is no jump
        const lastF = n ? Number(cards[n - 1].style.getPropertyValue('--f')) || 0 : 1;
        if (last) last.style.setProperty('--lift', clamp01(lastF * 4).toFixed(4));
        lines.forEach((line) => line.classList.toggle('is-on', Number(line.dataset.pcLine) === top && p < span + 0.04));
        // the photo starts growing while the last card is still flying off, then the headline rises
        const e = easeInOut(clamp01((ms - 150) / 1700));
        const t = ease(clamp01((ms - 1500) / 1200));
        this.classList.toggle('is-opening', e > 0.001);
        this.style.setProperty('--e', e.toFixed(4));
        this.style.setProperty('--t', t.toFixed(4));
        this.classList.toggle('is-titling', t > 0);
        this.classList.toggle('is-done', t > 0.7);
      }
      else if (this.mode === 'slices') {
        // Start the slide-in while the section scrolls into view, then lock and reveal while pinned
        p = clamp01((vh - r.top) / (r.height));
        const ease = (t) => 1 - Math.pow(1 - t, 3);
        const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
        const a = ease(clamp01((p - 0.05) / 0.45));
        const b = easeInOut(clamp01((p - 0.42) / 0.3));
        const c = ease(clamp01((p - 0.7) / 0.18));
        this.style.setProperty('--a', a.toFixed(4));
        this.style.setProperty('--b', b.toFixed(4));
        this.style.setProperty('--c', c.toFixed(4));
        this.classList.toggle('is-done', c > 0.5);
      }
      else if (this.mode === 'rail') {
        if (this.classList.contains('rail--swipe')) return;
        p = clamp01(-r.top / Math.max(1, r.height - vh));
        const x = this.reverse
          ? (1 - p) * (this.distance || 0)
          : Math.min(Math.max(-r.top, 0), (this.distance || 0) + (this.enter || 0)) - (this.enter || 0);
        this.style.setProperty('--rail-x', x.toFixed(1));
        if (this.reverse) {
          this.style.setProperty('--p', p.toFixed(4));
          this.updateCounter(p);
          return;
        }
        // The card nearest the centre of the screen is active; the road fills up to that centre line
        const mid = this.viewport.clientWidth / 2 + x;
        let best = 0;
        let bestDist = Infinity;
        this.items.forEach((item, i) => {
          const d = Math.abs(item.offsetLeft + item.offsetWidth / 2 - mid);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        const road = this.roadBox || { x: 0, w: 1 };
        this.style.setProperty('--p', clamp01((mid - road.x) / Math.max(1, road.w)).toFixed(4));
        this.setCurrent(best);
        return;
      } else p = clamp01(-r.top / Math.max(1, r.height - vh));
      this.style.setProperty('--p', p.toFixed(4));
    }
  }
  function requestSceneFrame() {
    if (sceneTicking) return;
    sceneTicking = true;
    requestAnimationFrame(() => { activeScenes.forEach((s) => s.update()); sceneTicking = false; });
  }
  window.addEventListener('scroll', requestSceneFrame, { passive: true });
  window.addEventListener('resize', requestSceneFrame);
  customElements.define('scroll-scene', ScrollScene);

  /* ---------------------------------------------------------------------
     Language switcher + browser language suggestion
     --------------------------------------------------------------------- */
  customElements.define('language-switcher', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const opt = e.target.closest('[data-value]');
        if (!opt) return;
        e.preventDefault();
        if (opt.classList.contains('is-active')) return;
        const form = $('form', this);
        $('input[name="language_code"]', form).value = opt.dataset.value;
        try { localStorage.setItem('aksheya-lang-choice', opt.dataset.value); } catch (err) { /* storage blocked */ }
        form.submit();
      });
    }
  });


  /* ---------------------------------------------------------------------
     Cookie consent: first-visit dialog wired to Shopify's Customer Privacy API
     --------------------------------------------------------------------- */
  customElements.define('cookie-consent', class extends HTMLElement {
    connectedCallback() {
      this.key = 'aksheya-consent';
      this.addEventListener('click', (e) => {
        const action = e.target.closest('[data-action]')?.dataset.action;
        if (action === 'accept') this.save({ preferences: true, analytics: true, marketing: true });
        if (action === 'decline') this.save({ preferences: false, analytics: false, marketing: false });
        if (action === 'customise') this.view('prefs');
        if (action === 'save') this.save(Object.fromEntries($$('[data-consent]', this).map((i) => [i.dataset.consent, i.checked])));
      });
      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-cookie-settings]')) return;
        e.preventDefault();
        this.open('prefs');
      });
      const start = () => {
        this.api = window.Shopify?.customerPrivacy;
        if (this.pending()) this.open('intro');
        else window.dispatchEvent(new CustomEvent('aksheya:consent'));
      };
      if (window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => start());
      } else start();
    }
    stored() {
      try { return JSON.parse(localStorage.getItem(this.key) || 'null'); } catch (err) { return null; }
    }
    pending() {
      const c = this.api?.currentVisitorConsent?.();
      if (c) return !c.analytics && !c.marketing && !c.preferences;
      return !this.stored();
    }
    current() {
      const c = this.api?.currentVisitorConsent?.();
      if (c && (c.analytics || c.marketing || c.preferences)) {
        return { preferences: c.preferences === 'yes', analytics: c.analytics === 'yes', marketing: c.marketing === 'yes' };
      }
      return this.stored() || { preferences: false, analytics: false, marketing: false };
    }
    view(name) {
      $$('[data-view]', this).forEach((el) => { el.hidden = el.dataset.view !== name; });
      if (name === 'prefs') {
        const cur = this.current();
        $$('[data-consent]', this).forEach((i) => { i.checked = !!cur[i.dataset.consent]; });
      }
    }
    open(name) {
      this.view(name);
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-visible'));
    }
    close() {
      this.classList.remove('is-visible');
      setTimeout(() => { this.hidden = true; }, 350);
    }
    save(prefs) {
      try { localStorage.setItem(this.key, JSON.stringify(prefs)); } catch (err) { /* storage blocked */ }
      const done = () => window.dispatchEvent(new CustomEvent('aksheya:consent', { detail: prefs }));
      if (this.api?.setTrackingConsent) {
        this.api.setTrackingConsent({
          analytics: prefs.analytics, marketing: prefs.marketing, preferences: prefs.preferences, sale_of_data: prefs.marketing,
        }, done);
      } else done();
      this.close();
    }
  });
  customElements.define('language-suggest', class extends HTMLElement {
    connectedCallback() {
      let chosen = null;
      try { chosen = localStorage.getItem('aksheya-lang-choice') || localStorage.getItem('aksheya-lang-dismissed'); } catch (err) { /* storage blocked */ }
      if (chosen) return;
      const current = this.dataset.current;
      const prefs = (navigator.languages || [navigator.language || '']).map((l) => l.toLowerCase().split('-')[0]);
      const match = prefs.find((code) => $(`template[data-lang="${code}"]`, this));
      if (!match || match === current) return;
      const tpl = $(`template[data-lang="${match}"]`, this);
      $('[data-body]', this).appendChild(tpl.content.cloneNode(true));
      // wait until the cookie dialog has been answered, so the two never stack on top of each other
      const show = () => { this.hidden = false; requestAnimationFrame(() => this.classList.add('is-visible')); };
      const consent = $('cookie-consent');
      if (consent && !consent.hidden) window.addEventListener('aksheya:consent', () => setTimeout(show, 600), { once: true });
      else if (consent) window.addEventListener('aksheya:consent', show, { once: true });
      else show();
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-dismiss]')) {
          try { localStorage.setItem('aksheya-lang-dismissed', current); } catch (err) { /* storage blocked */ }
          this.classList.remove('is-visible');
          setTimeout(() => { this.hidden = true; }, 300);
        }
        const sw = e.target.closest('[data-switch]');
        if (sw) {
          const form = $('form', this);
          $('input[name="language_code"]', form).value = sw.dataset.switch;
          try { localStorage.setItem('aksheya-lang-choice', sw.dataset.switch); } catch (err) { /* storage blocked */ }
          form.submit();
        }
      });
    }
  });

  /* ---------------------------------------------------------------------
     Newsletter: submit in place and cross-fade to the thank-you state
     --------------------------------------------------------------------- */
  // Captured on window so it runs before Shopify's own captcha handler on the form
  window.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-newsletter]');
    if (!form || form.dataset.native) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (form.dataset.busy) return;
    form.dataset.busy = '1';
    const button = $('button[type=submit]', form);
    const error = $('[data-newsletter-error]', form);
    if (button) button.disabled = true;
    try {
      // Shopify protects the form with invisible hCaptcha: get its token before posting in place
      // The widget loads lazily; wait for it briefly, otherwise let Shopify's normal post handle it
      let frame = null;
      for (let i = 0; i < 25 && !(frame && window.hcaptcha); i += 1) {
        frame = $('iframe[data-hcaptcha-widget-id]', form);
        if (!(frame && window.hcaptcha)) await new Promise((r) => setTimeout(r, 200));
      }
      const tokenField = $('[name="h-captcha-response"]', form);
      if ($('.h-captcha', form) && !(frame && window.hcaptcha && tokenField)) throw new Error('captcha not ready');
      if (frame && window.hcaptcha && tokenField) {
        const { response } = await window.hcaptcha.execute(frame.dataset.hcaptchaWidgetId, { async: true });
        tokenField.value = response;
      }
      const res = await fetch(form.action, { method: 'POST', body: new FormData(form) });
      if (res.status === 400) throw new Error('captcha rejected');
      if (frame && window.hcaptcha) { try { window.hcaptcha.reset(frame.dataset.hcaptchaWidgetId); } catch (x) { /* ignore */ } }
      const url = new URL(res.url);
      // Shopify may ask for a captcha: fall back to the normal form post
      if (url.pathname.includes('challenge')) { form.dataset.native = '1'; form.requestSubmit(); return; }
      if (url.searchParams.get('customer_posted') === 'true') {
        $$('[data-newsletter-fields]', form).forEach((el) => el.classList.add('is-leaving'));
        error.hidden = true;
        setTimeout(() => {
          $$('[data-newsletter-fields]', form).forEach((el) => { el.hidden = true; });
          const done = $('[data-newsletter-done]', form);
          done.hidden = false;
          requestAnimationFrame(() => done.classList.add('is-in'));
        }, 280);
        return;
      }
      const doc = parseHTML(await res.text());
      const message = doc.querySelector(`#${form.id} .form-message--error`);
      error.textContent = message ? message.textContent.trim() : theme.strings.error;
      error.hidden = false;
    } catch (err) {
      form.dataset.native = '1';
      form.requestSubmit();
      return;
    } finally {
      delete form.dataset.busy;
    }
    if (button) button.disabled = false;
  }, true);
  // Without JS (or after a captcha) the page reloads on #Newsletter-…: keep the whole block in view, not just the form
  if (/^#Newsletter-/.test(location.hash)) {
    const target = document.getElementById(location.hash.slice(1));
    const block = target && (target.closest('.footer__news, .newsletter') || target);
    if (block) requestAnimationFrame(() => block.scrollIntoView({ block: 'center' }));
    $$('[data-newsletter-done]').forEach((el) => el.classList.add('is-in'));
  }

  /* ---------------------------------------------------------------------
     Account links: show the signed-in dot once the account panel has a session
     --------------------------------------------------------------------- */
  try {
    if (localStorage.getItem('aksheya.account')) $$('[data-account-link]').forEach((a) => a.classList.add('icon-btn--signed-in'));
  } catch (e) { /* storage blocked */ }
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-account-logout]')) return;
    try { localStorage.removeItem('aksheya.account'); } catch (err) { /* storage blocked */ }
  });

  /* ---------------------------------------------------------------------
     Cart extras: invoice details (individual / corporate, billing address), gift wrap / message as cart attributes
     --------------------------------------------------------------------- */
  // Fields that are visible and filled wrongly: required ones left empty, digit fields with the wrong length
  const invoiceProblems = (root) => $$('[data-invoice] [data-cart-attr]', root).filter((el) => {
    if (el.closest('[hidden]')) return false;
    const value = el.value.trim();
    if (el.dataset.digits && value) return !el.dataset.digits.split(',').includes(String(value.length)) || /\D/.test(value);
    return el.hasAttribute('data-required') && value.length < 2;
  });
  const markInvoice = (root) => {
    const bad = invoiceProblems(root);
    $$('[data-invoice] [data-cart-attr]', root).forEach((el) => el.toggleAttribute('aria-invalid', bad.includes(el)));
    const error = $('[data-invoice-error]', root);
    if (error) error.hidden = !bad.length;
    return bad;
  };
  // "Ödemeye geç": agreements must be accepted in the cart, then the invoice step opens; its
  // "Ödemeye devam et" button submits the same form once the invoice details are complete.
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.id || !e.submitter || e.submitter.name !== 'checkout') return;
    const consent = $('[data-consent]', form);
    const box = consent && $('[data-consent-box]', consent);
    if (box && !box.checked) {
      e.preventDefault();
      $('[data-consent-error]', consent).hidden = false;
      box.focus();
      return;
    }
    const step = document.getElementById(`Invoice-${form.id}`);
    if (!step) return;
    if (e.submitter.matches('[data-invoice-continue]')) {
      const bad = markInvoice(step);
      if (bad.length) { e.preventDefault(); bad[0].focus(); }
      return;
    }
    e.preventDefault();
    step.showModal();
  });

  /* Agreements: open the texts in a dialog, record the acceptance on the order */
  const setConsent = (box, checked) => {
    const consent = box.closest('[data-consent]');
    box.checked = checked;
    const value = checked ? `Kabul edildi · ${new Date().toISOString()}` : '';
    $('[data-consent-value]', consent).value = value;
    if (checked) $('[data-consent-error]', consent).hidden = true;
    fetch(`${theme.routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Sözleşme onayı': value } }),
    });
  };
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-consent-box]')) setConsent(e.target, e.target.checked);
  });
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-contract-open]');
    if (opener) {
      e.preventDefault();
      const dialog = document.getElementById(opener.dataset.contractOpen);
      if (dialog && !dialog.open) dialog.showModal();
      return;
    }
    const dialog = e.target.closest('dialog.contract');
    if (!dialog) return;
    if (e.target === dialog || e.target.closest('[data-contract-close]')) { dialog.close(); return; }
    if (e.target.closest('[data-contract-accept]')) {
      const form = dialog.id.replace(/^(Pre|Sale)-/, '');
      const box = document.querySelector(`[data-consent-box][form="${form}"]`);
      if (box) setConsent(box, true);
      dialog.close();
    }
  });
  customElements.define('cart-extras', class extends HTMLElement {
    connectedCallback() {
      // Collect changes from several fields and send them together
      let pending = {};
      const flush = debounce(() => {
        const attributes = pending;
        pending = {};
        fetch(`${theme.routes.cartUpdate}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes }),
        });
      }, 400);
      const save = (attrs) => { Object.assign(pending, attrs); flush(); };
      this.addEventListener('change', async (e) => {
        // Clearing a hidden group also clears its cart attributes, so the order only carries what applies
        const clear = (group, attrs) => $$('[data-cart-attr]', group).forEach((el) => { el.value = ''; el.removeAttribute('aria-invalid'); attrs[el.dataset.cartAttr] = ''; });
        const choice = e.target.closest('[data-invoice-choice]');
        if (choice) {
          const type = choice.value;
          const attrs = { 'Fatura tipi': type };
          $('[data-invoice-type]', this).value = type;
          $$('[data-invoice-panel]', this).forEach((panel) => {
            panel.hidden = panel.dataset.invoicePanel !== type;
            if (panel.hidden) clear(panel, attrs);
          });
          save(attrs);
          $(`[data-invoice-panel="${type}"] input`, this)?.focus();
          if (!$('[data-invoice-error]', this).hidden) markInvoice(this);
          return;
        }
        const billing = e.target.closest('[data-billing-toggle]');
        if (billing) {
          const group = $('[data-billing-fields]', this);
          const attrs = {};
          group.hidden = !billing.checked;
          if (!billing.checked) clear(group, attrs);
          save(attrs);
          if (billing.checked) $('input', group).focus();
          if (!$('[data-invoice-error]', this).hidden) markInvoice(this);
          return;
        }
        const toggle = e.target.closest('[data-gift-toggle]');
        if (!toggle) return;
        toggle.disabled = true;
        try {
          if (toggle.checked) {
            const fd = new FormData();
            fd.append('id', toggle.value);
            fd.append('quantity', '1');
            await Cart.add(fd);
          } else {
            const res = await fetch(`${theme.routes.cartChange}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: toggle.value, quantity: 0, sections: Cart.sections(), sections_url: window.location.pathname }),
            });
            const data = await res.json();
            await Cart.render(data.sections);
            save({ 'Hediye notu': '' });
          }
        } catch (err) {
          toast(err.message);
          toggle.checked = !toggle.checked;
          toggle.disabled = false;
        }
      });
      this.addEventListener('input', (e) => {
        const el = e.target.closest('[data-cart-attr]');
        if (!el) return;
        if (el.dataset.digits) el.value = el.value.replace(/\D/g, '');
        save({ [el.dataset.cartAttr]: el.value });
        const error = $('[data-invoice-error]', this);
        if (error && !error.hidden) markInvoice(this);
      });
    }
  });

  /* ---------------------------------------------------------------------
     Shoppable Instagram loop: tap to open a tagged product on touch devices
     --------------------------------------------------------------------- */
  customElements.define('ig-loop', class extends HTMLElement {
    connectedCallback() {
      const touch = window.matchMedia('(hover: none)');
      const closeAll = () => {
        $$('.ig-feed__item.is-open', this).forEach((el) => el.classList.remove('is-open'));
        this.classList.remove('is-holding');
      };
      this.addEventListener('click', (e) => {
        const item = e.target.closest('[data-ig-tagged]');
        if (!item || !touch.matches) return;
        if (e.target.closest('.ig-tag__btn')) return;
        e.preventDefault();
        const open = item.classList.contains('is-open');
        closeAll();
        if (!open) { item.classList.add('is-open'); this.classList.add('is-holding'); }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAll();
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) closeAll(); });
      // Pause the loop while a tagged tile is keyboard-focused
      this.addEventListener('focusin', () => this.classList.add('is-holding'));
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) closeAll(); });

      // Live posts from the (hidden) Instafeed widget replace the manual photos
      this.overlayIcon = $('.ig-feed__overlay', this)?.innerHTML || '';
      const build = (json) => this.renderPosts(json);
      if (window.__aksheyaIgFeed) build(window.__aksheyaIgFeed);
      document.addEventListener('aksheya:igfeed', (e) => build(e.detail));
    }

    // Finds the product a caption talks about. A product counts as named when its full title appears
    // ("Black Sea Whisper Set"), or its name without the garment word appears followed by a garment word
    // ("Glamore Set" for "Glamore Skirt"), or that name is a distinctive phrase ("White Lady"). Longest match wins.
    productMatcher() {
      const GARMENT = 'set|takım|takim|dress|elbise|skirt|etek|top|bluz|shirt|gömlek|gomlek|jacket|ceket|pants|pantolon|corset|korse|layer|scarf|bolero|aksesuar|accessory';
      const norm = (t) => String(t).toLocaleLowerCase('tr')
        .replace(/[#@][^\s]+/g, ' ')
        .replace(/['’`´]/g, '')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
      const garment = new RegExp(`\\s(?:${GARMENT})$`);
      const names = [];
      $$('template[data-ig-product]', this.closest('.ig-feed')).forEach((t) => {
        const full = norm(t.dataset.title);
        if (!full) return;
        names.push({ text: full, template: t, needsGarment: false });
        const base = norm(t.dataset.title.split(/[–—-]/)[0]).replace(garment, '').trim();
        // Short or everyday-phrase names ("Nazar", "Fall in Love") only count when a garment word follows them
        const everyday = /(^| )(in|for|you|its|to|be|i|said|yes|the|my|of|and|ve|bir)( |$)/.test(base);
        if (base && base !== full && base.length >= 4) names.push({ text: base, template: t, needsGarment: !base.includes(' ') || everyday });
      });
      names.sort((a, b) => b.text.length - a.text.length);
      return (caption) => {
        const text = ` ${norm(caption)} `;
        const hit = names.find((n) => (n.needsGarment
          ? new RegExp(` ${n.text} (?:${GARMENT}) `).test(text)
          : text.includes(` ${n.text} `)));
        return hit ? hit.template : null;
      };
    }

    renderPosts(json) {
      const posts = (json && json.data) || [];
      if (!posts.length) return;
      const code = (url) => (String(url || '').match(/instagram\.com\/(?:[^/]+\/)?(?:p|reel|tv)\/([^/?#]+)/) || [])[1];
      const tags = {};
      $$('template[data-ig-post]', this.closest('.ig-feed')).forEach((t) => { const c = code(t.dataset.igPost); if (c) tags[c] = t; });
      const auto = this.productMatcher();
      const limit = parseInt(this.dataset.limit, 10) || 12;
      const alt = this.dataset.alt || 'Instagram';
      const esc = (v) => String(v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const html = posts.slice(0, limit).map((p) => {
        const src = p.images && p.images.standard_resolution && p.images.standard_resolution.url;
        if (!src) return '';
        const caption = ((p.caption && p.caption.text) || alt).slice(0, 120);
        const tag = tags[code(p.link)] || auto((p.caption && p.caption.text) || '');
        const img = `<img src="${esc(src)}" alt="${esc(caption)}" loading="lazy" decoding="async">`;
        if (tag) return `<div class="ig-feed__item ig-feed__item--tagged" tabindex="0" data-ig-tagged>${img}${tag.innerHTML}</div>`;
        return `<div class="ig-feed__item">${img}<a href="${esc(p.link || this.dataset.igUrl || '#')}" class="ig-feed__link" target="_blank" rel="noopener" aria-label="Instagram"><span class="ig-feed__overlay" aria-hidden="true">${this.overlayIcon}</span></a></div>`;
      }).join('');
      if (!html) return;
      $$('.ig-feed__set', this).forEach((set) => { set.innerHTML = html; });
      this.classList.add('is-live');
    }
  });

  // Footer wordmark: one-time reveal, shine only while visible, cursor spotlight on desktop
  $$('[data-heartbeat]').forEach((el) => {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) el.setAttribute('data-ready', '');
    new IntersectionObserver(([entry]) => {
      el.classList.toggle('is-beating', entry.isIntersecting);
      if (entry.isIntersecting && entry.intersectionRatio > 0.35) el.classList.add('is-revealed');
    }, { threshold: [0, 0.35, 0.6] }).observe(el);
    const inner = el.querySelector('.footer__wordmark-inner');
    if (!inner || reduce || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    el.addEventListener('pointermove', (e) => {
      const r = inner.getBoundingClientRect();
      inner.style.setProperty('--mx', `${e.clientX - r.left}px`);
      inner.style.setProperty('--my', `${e.clientY - r.top}px`);
      inner.classList.add('is-lit');
    });
    el.addEventListener('pointerleave', () => inner.classList.remove('is-lit'));
  });

  /* ---------------------------------------------------------------------
     Cart page wrapper (marker element for section rendering)
     --------------------------------------------------------------------- */
  customElements.define('cart-page', class extends HTMLElement {});

  /* ---------------------------------------------------------------------
     App bar: shrink while scrolling down, expand on scroll up
     --------------------------------------------------------------------- */
  const appBar = $('[data-app-bar]');
  if (appBar) {
    let lastY = window.scrollY;
    let ticking = false;
    const setCompact = (on) => {
      appBar.classList.toggle('is-compact', on);
      document.body.classList.toggle('appbar-compact', on);
    };
    const update = () => {
      const y = window.scrollY;
      const nearBottom = window.innerHeight + y >= document.documentElement.scrollHeight - 40;
      if (y < 80 || nearBottom) setCompact(false);
      else if (y > lastY + 6) setCompact(true);
      else if (y < lastY - 6) setCompact(false);
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    appBar.addEventListener('click', (e) => {
      if (appBar.classList.contains('is-compact') && e.target.closest('.app-bar__item')) setCompact(false);
    });
  }

  /* ---------------------------------------------------------------------
     Reveal on scroll + footer accordions
     --------------------------------------------------------------------- */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-inview');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' })
    : null;
  function initReveal(ctx = document) {
    $$('.reveal:not(.is-inview)', ctx).forEach((el) => {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-inview');
    });
  }
  initReveal();

  const syncFooter = () => {
    $$('[data-footer-accordion]').forEach((d) => { d.open = mqDesktop.matches; });
  };
  syncFooter();
  mqDesktop.addEventListener('change', syncFooter);

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
  document.addEventListener('shopify:section:load', syncFooter);
})();
